import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useToast } from '../../hooks/use-toast';
import { CreditCard, Search, Plus, Edit } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

const Limits = () => {
  const [customers, setCustomers] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [limits, setLimits] = useState(null);
  const [overdraftLimit, setOverdraftLimit] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [reason, setReason] = useState('');
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    pix_daily_limit: 5000,
    pix_tx_limit: 1000,
    boleto_daily_limit: 10000
  });

  const [overdraftData, setOverdraftData] = useState({
    limit_total: 1000,
    interest_rate_monthly: 0.0499
  });

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/admin/customers');
      setCustomers(response.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const handleSelectCustomer = async (customer) => {
    setLoading(true);
    try {
      // Get account
      const accountResponse = await api.get(`/admin/customers/${customer.id}/account`);
      const account = accountResponse.data;
      setSelectedAccount({ ...account, customer });

      // Get operational limits
      const limitsResponse = await api.get(`/admin/limits/${account.id}`);
      if (limitsResponse.data) {
        setLimits(limitsResponse.data);
        setFormData({
          pix_daily_limit: limitsResponse.data.pix_daily_limit,
          pix_tx_limit: limitsResponse.data.pix_tx_limit,
          boleto_daily_limit: limitsResponse.data.boleto_daily_limit
        });
      } else {
        setLimits(null);
      }

      // Get overdraft limit
      const detailResponse = await api.get(`/admin/customers/${customer.id}/detail`);
      if (detailResponse.data.overdraft_limit) {
        setOverdraftLimit(detailResponse.data.overdraft_limit);
        setOverdraftData({
          limit_total: detailResponse.data.overdraft_limit.limit_total,
          interest_rate_monthly: detailResponse.data.overdraft_limit.interest_rate_monthly
        });
      } else {
        setOverdraftLimit(null);
      }
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: 'Erro ao buscar dados da conta',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLimits = async () => {
    if (!reason.trim()) {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: 'Informe o motivo da criação',
      });
      return;
    }

    try {
      await api.post('/admin/limits', {
        account_id: selectedAccount.id,
        ...formData
      });
      toast({
        title: 'Limites criados!',
        description: 'Limites operacionais definidos com sucesso.',
      });
      setDialogOpen(false);
      setReason('');
      handleSelectCustomer(selectedAccount.customer);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: error.response?.data?.detail || 'Erro ao criar limites',
      });
    }
  };

  const handleUpdateLimits = async () => {
    if (!reason.trim()) {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: 'Informe o motivo da atualização',
      });
      return;
    }

    try {
      await api.put(`/admin/limits/${limits.id}`, {
        ...formData,
        reason
      });
      toast({
        title: 'Limites atualizados!',
        description: 'Limites operacionais atualizados com sucesso.',
      });
      setDialogOpen(false);
      setEditMode(false);
      setReason('');
      handleSelectCustomer(selectedAccount.customer);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: error.response?.data?.detail || 'Erro ao atualizar limites',
      });
    }
  };

  const handleUpdateOverdraft = async () => {
    if (!reason.trim()) {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: 'Informe o motivo da atualização',
      });
      return;
    }

    try {
      await api.post(`/admin/overdraft/${selectedAccount.id}/set-limit`, overdraftData);
      toast({
        title: 'Cheque especial atualizado!',
        description: 'Limite de cheque especial atualizado com sucesso.',
      });
      handleSelectCustomer(selectedAccount.customer);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: error.response?.data?.detail || 'Erro ao atualizar cheque especial',
      });
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const filteredCustomers = customers.filter((customer) => {
    const matchesSearch =
      customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.document.includes(searchTerm);
    const matchesType = typeFilter === 'ALL' || customer.type === typeFilter;
    return matchesSearch && matchesType;
  });

  return (
    <div className="space-y-6" data-testid="limits-page">
      <div>
        <h1 className="text-4xl font-bold tracking-tight">Gerenciamento de Limites</h1>
        <p className="text-muted-foreground mt-2">Configure limites operacionais e cheque especial</p>
      </div>

      {/* Customer Search */}
      <Card className="border-border/50">
        <CardHeader>
          <CardTitle>Selecionar Cliente</CardTitle>
          <CardDescription>Busque por nome, email ou documento</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-2 md:grid-cols-[1fr_12rem]">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar cliente..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger aria-label="Filtrar por tipo de cliente">
                <SelectValue placeholder="Todos os tipos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Todos</SelectItem>
                <SelectItem value="PF">Pessoa Física</SelectItem>
                <SelectItem value="PJ">Pessoa Jurídica</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {(searchTerm || typeFilter !== 'ALL') && (
            <div className="max-h-60 overflow-auto space-y-2">
              {filteredCustomers.map((customer) => (
                <div
                  key={customer.id}
                  className="flex items-center justify-between p-3 rounded-lg border border-border/50 hover:bg-muted/50 cursor-pointer"
                  onClick={() => handleSelectCustomer(customer)}
                >
                  <div>
                    <p className="font-medium">{customer.name}</p>
                    <p className="text-sm text-muted-foreground">{customer.email} | {customer.document}</p>
                  </div>
                  <div className="flex gap-2">
                    <Badge variant="outline">{customer.type}</Badge>
                    <Badge variant={customer.status === 'ACTIVE' ? 'default' : 'secondary'}>
                      {customer.status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Selected Account Info */}
      {selectedAccount && (
        <Card className="border-border/50 bg-muted/20">
          <CardHeader>
            <CardTitle>Conta Selecionada</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Cliente</p>
                <p className="font-medium">{selectedAccount.customer.name}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Classificação</p>
                <Badge variant="outline">{selectedAccount.customer.type}</Badge>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">ID da Conta</p>
                <p className="font-mono font-medium">{selectedAccount.id}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Status</p>
                <Badge>{selectedAccount.status}</Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Operational Limits */}
      {selectedAccount && (
        <Card className="border-border/50">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Limites Operacionais</CardTitle>
                <CardDescription>Limites diários de transações</CardDescription>
              </div>
              {limits ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-full"
                  onClick={() => {
                    setEditMode(true);
                    setDialogOpen(true);
                  }}
                >
                  <Edit className="mr-2 h-4 w-4" />
                  Editar
                </Button>
              ) : (
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="rounded-full">
                      <Plus className="mr-2 h-4 w-4" />
                      Criar Limites
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Criar Limites Operacionais</DialogTitle>
                      <DialogDescription>Defina os limites para esta conta</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>Pix Diário</Label>
                        <Input
                          type="number"
                          value={formData.pix_daily_limit}
                          onChange={(e) => setFormData({ ...formData, pix_daily_limit: parseFloat(e.target.value) })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Pix por Transação</Label>
                        <Input
                          type="number"
                          value={formData.pix_tx_limit}
                          onChange={(e) => setFormData({ ...formData, pix_tx_limit: parseFloat(e.target.value) })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Boleto Diário</Label>
                        <Input
                          type="number"
                          value={formData.boleto_daily_limit}
                          onChange={(e) => setFormData({ ...formData, boleto_daily_limit: parseFloat(e.target.value) })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Motivo</Label>
                        <Textarea
                          placeholder="Descreva o motivo da criação dos limites..."
                          value={reason}
                          onChange={(e) => setReason(e.target.value)}
                          rows={3}
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                      <Button onClick={handleCreateLimits}>Criar</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="py-8 text-center">
                <div className="h-6 w-6 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto" />
              </div>
            ) : limits ? (
              <div className="grid md:grid-cols-3 gap-4">
                <div className="rounded-lg border border-border/50 p-4">
                  <p className="text-sm text-muted-foreground mb-2">Pix Diário</p>
                  <p className="text-2xl font-bold font-mono">{formatCurrency(limits.pix_daily_limit)}</p>
                </div>
                <div className="rounded-lg border border-border/50 p-4">
                  <p className="text-sm text-muted-foreground mb-2">Pix por Transação</p>
                  <p className="text-2xl font-bold font-mono">{formatCurrency(limits.pix_tx_limit)}</p>
                </div>
                <div className="rounded-lg border border-border/50 p-4">
                  <p className="text-sm text-muted-foreground mb-2">Boleto Diário</p>
                  <p className="text-2xl font-bold font-mono">{formatCurrency(limits.boleto_daily_limit)}</p>
                </div>
              </div>
            ) : (
              <div className="py-8 text-center text-muted-foreground">
                Nenhum limite configurado. Clique em "Criar Limites" para começar.
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Edit Dialog */}
      {editMode && (
        <Dialog open={editMode} onOpenChange={setEditMode}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Editar Limites Operacionais</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Pix Diário</Label>
                <Input
                  type="number"
                  value={formData.pix_daily_limit}
                  onChange={(e) => setFormData({ ...formData, pix_daily_limit: parseFloat(e.target.value) })}
                />
              </div>
              <div className="space-y-2">
                <Label>Pix por Transação</Label>
                <Input
                  type="number"
                  value={formData.pix_tx_limit}
                  onChange={(e) => setFormData({ ...formData, pix_tx_limit: parseFloat(e.target.value) })}
                />
              </div>
              <div className="space-y-2">
                <Label>Boleto Diário</Label>
                <Input
                  type="number"
                  value={formData.boleto_daily_limit}
                  onChange={(e) => setFormData({ ...formData, boleto_daily_limit: parseFloat(e.target.value) })}
                />
              </div>
              <div className="space-y-2">
                <Label>Motivo da Alteração</Label>
                <Textarea
                  placeholder="Descreva o motivo da alteração..."
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditMode(false)}>Cancelar</Button>
              <Button onClick={handleUpdateLimits}>Atualizar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Overdraft Limit */}
      {selectedAccount && (
        <Card className="border-border/50">
          <CardHeader>
            <CardTitle>Cheque Especial</CardTitle>
            <CardDescription>Limite de crédito emergencial</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {overdraftLimit ? (
              <div className="grid md:grid-cols-2 gap-4">
                <div className="rounded-lg border border-border/50 p-4">
                  <p className="text-sm text-muted-foreground mb-2">Limite Total</p>
                  <p className="text-2xl font-bold font-mono">{formatCurrency(overdraftLimit.limit_total)}</p>
                </div>
                <div className="rounded-lg border border-border/50 p-4">
                  <p className="text-sm text-muted-foreground mb-2">Taxa Mensal</p>
                  <p className="text-2xl font-bold font-mono">{(overdraftLimit.interest_rate_monthly * 100).toFixed(2)}%</p>
                </div>
              </div>
            ) : (
              <div className="py-4 text-center text-muted-foreground">
                Nenhum limite de cheque especial configurado
              </div>
            )}

            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Novo Limite Total</Label>
                  <Input
                    type="number"
                    value={overdraftData.limit_total}
                    onChange={(e) => setOverdraftData({ ...overdraftData, limit_total: parseFloat(e.target.value) })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Taxa de Juros Mensal (%)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={(overdraftData.interest_rate_monthly * 100).toFixed(2)}
                    onChange={(e) => setOverdraftData({ ...overdraftData, interest_rate_monthly: parseFloat(e.target.value) / 100 })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Motivo</Label>
                <Textarea
                  placeholder="Descreva o motivo da alteração do cheque especial..."
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={2}
                />
              </div>
              <Button onClick={handleUpdateOverdraft} className="rounded-full">
                <CreditCard className="mr-2 h-4 w-4" />
                {overdraftLimit ? 'Atualizar' : 'Criar'} Cheque Especial
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Limits;
